/**
 * Greeting-card picker on the product page (spec tasks/specs/wenskaart-kiezen/spec.md).
 *
 * Loads the cards of the card collection once, on first open, through the Section Rendering API
 * (sections/gift-card-options.liquid), filters them by name as the customer types, and writes the
 * chosen card onto the slot's data attributes. assets/product-main.js reads those attributes on
 * submit to add the card line, and listens for `gift-card:change` to keep the button total right.
 */

const slot = document.querySelector('[data-gift-card-slot]');
const picker = /** @type {(HTMLElement & { showDialog?: () => void, closeDialog?: () => Promise<void> }) | null} */ (
  document.querySelector('[data-gift-card-dialog]')
);

if (slot instanceof HTMLElement && picker) {
  const addButton = slot.querySelector('.product-main__card-add');
  const chosen = slot.querySelector('[data-gift-card-chosen]');
  const chosenThumb = slot.querySelector('[data-gift-card-thumb]');
  const chosenTitle = slot.querySelector('[data-gift-card-title]');
  const chosenPrice = slot.querySelector('[data-gift-card-price]');
  const text = slot.querySelector('[data-gift-card-text]');
  const count = slot.querySelector('[data-gift-card-count]');

  const search = picker.querySelector('[data-gift-card-search]');
  const status = picker.querySelector('[data-gift-card-status]');
  const retry = picker.querySelector('[data-gift-card-retry]');
  const results = picker.querySelector('[data-gift-card-results]');

  const MAX_LENGTH = Number(text?.getAttribute('maxlength')) || 250;

  /** @type {Promise<void> | null} */
  let loading = null;
  let loaded = false;

  /** Focus target once the dialog has closed after a pick or a remove. */
  /** @type {HTMLElement | null} */
  let focusAfterClose = null;

  /**
   * @param {string} message
   */
  const setStatus = (message) => {
    if (!status) return;
    status.textContent = message;
    status.hidden = message === '';
  };

  /**
   * Fetches every page of the rendered card grid and appends its items to the results.
   *
   * @returns {Promise<void>}
   */
  const loadCards = async () => {
    const grid = document.createElement('ul');
    grid.className = 'gift-card-picker__grid';
    grid.setAttribute('role', 'list');

    /** @type {string | null} */
    let url = slot.dataset.giftCardSource ?? null;

    while (url) {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`gift-card-options ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const page = html.querySelector('[data-gift-card-grid]');
      if (!page) throw new Error('gift-card-options: no grid');

      grid.append(...page.children);

      // Every further page is rendered by the same section, so it keeps the section_id.
      const next = html.querySelector('[data-gift-card-next]')?.getAttribute('href');
      if (next) {
        const nextUrl = new URL(next, window.location.origin);
        nextUrl.searchParams.set('section_id', 'gift-card-options');
        url = nextUrl.pathname + nextUrl.search;
      } else {
        url = null;
      }
    }

    results?.replaceChildren(grid);
    loaded = true;
    markCurrent();
    filter();
  };

  const ensureLoaded = () => {
    if (loaded) return Promise.resolve();

    if (!loading) {
      setStatus('Wenskaarten laden...');
      if (retry instanceof HTMLElement) retry.hidden = true;

      loading = loadCards().catch(() => {
        setStatus('De wenskaarten konden niet worden geladen.');
        if (retry instanceof HTMLElement) retry.hidden = false;
      }).finally(() => {
        loading = null;
      });
    }

    return loading;
  };

  /** Shows only the cards whose name contains the search text, and says so when none do. */
  const filter = () => {
    if (!loaded) return;

    const query = (search instanceof HTMLInputElement ? search.value : '').trim().toLowerCase();
    let visible = 0;

    results?.querySelectorAll('[data-gift-card-name]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const match = query === '' || (item.dataset.giftCardName ?? '').includes(query);
      item.hidden = !match;
      if (match) visible += 1;
    });

    if (visible === 0) {
      setStatus(`Geen wenskaart gevonden voor "${query}".`);
    } else if (query !== '') {
      setStatus(visible === 1 ? '1 wenskaart gevonden' : `${visible} wenskaarten gevonden`);
    } else {
      setStatus('');
    }
  };

  /** Marks the chosen card in the grid, so reopening the picker shows what is selected now. */
  const markCurrent = () => {
    const current = slot.dataset.giftCardVariantId ?? '';

    results?.querySelectorAll('[data-gift-card-option]').forEach((option) => {
      if (!(option instanceof HTMLElement)) return;
      option.setAttribute('aria-pressed', String(option.dataset.variantId === current));
    });
  };

  const updateCount = () => {
    if (!(text instanceof HTMLTextAreaElement) || !count) return;
    count.textContent = `${text.value.length} van ${MAX_LENGTH} tekens`;
  };

  const announceChange = () => {
    slot.dispatchEvent(new CustomEvent('gift-card:change', { bubbles: true }));
  };

  /**
   * @param {HTMLElement} option - A card button from the grid.
   */
  const choose = (option) => {
    slot.dataset.giftCardVariantId = option.dataset.variantId ?? '';
    slot.dataset.giftCardTitle = option.dataset.title ?? '';
    slot.dataset.giftCardPriceCents = option.dataset.priceCents ?? '0';
    slot.dataset.ean = option.dataset.ean ?? '';

    if (chosenTitle) chosenTitle.textContent = option.dataset.title ?? '';
    if (chosenPrice) chosenPrice.textContent = `(+ ${option.dataset.priceLabel ?? ''})`;

    if (chosenThumb) {
      // The card's picture is decorative here: its name sits right beside it.
      const image = option.dataset.image;
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.width = 160;
        img.height = 160;
        chosenThumb.replaceChildren(img);
      } else {
        chosenThumb.replaceChildren();
      }
    }

    if (addButton instanceof HTMLElement) addButton.hidden = true;
    if (chosen instanceof HTMLElement) chosen.hidden = false;

    markCurrent();
    announceChange();

    focusAfterClose = text instanceof HTMLElement ? text : null;
    picker.closeDialog?.();
  };

  const remove = () => {
    delete slot.dataset.giftCardVariantId;
    delete slot.dataset.giftCardTitle;
    delete slot.dataset.giftCardPriceCents;
    delete slot.dataset.ean;

    // No card means no card text either (spec AC 5).
    if (text instanceof HTMLTextAreaElement) text.value = '';
    updateCount();

    if (chosen instanceof HTMLElement) chosen.hidden = true;
    if (addButton instanceof HTMLElement) {
      addButton.hidden = false;
      addButton.focus();
    }

    markCurrent();
    announceChange();
  };

  slot.querySelectorAll('[data-gift-card-open]').forEach((opener) => {
    opener.addEventListener('click', () => {
      picker.showDialog?.();
      ensureLoaded();

      // With a mouse, start in the search field so typing filters straight away. On touch the
      // native first focus (the close button) stays, so no keyboard covers the cards on open.
      // showDialog opens in the next animation frame, hence the wait.
      if (search instanceof HTMLInputElement && window.matchMedia('(pointer: fine)').matches) {
        requestAnimationFrame(() => requestAnimationFrame(() => search.focus()));
      }
    });
  });

  slot.querySelector('[data-gift-card-remove]')?.addEventListener('click', remove);

  results?.addEventListener('click', (event) => {
    const option = event.target instanceof Element ? event.target.closest('[data-gift-card-option]') : null;
    if (option instanceof HTMLElement && !option.hasAttribute('disabled')) choose(option);
  });

  search?.addEventListener('input', filter);
  retry?.addEventListener('click', () => ensureLoaded());
  text?.addEventListener('input', updateCount);

  picker.addEventListener('dialog:close', () => {
    // The native dialog hands focus back to whatever opened it. After a pick that opener is
    // hidden, so move on to the card text, the next thing to fill in.
    if (focusAfterClose && !focusAfterClose.closest('[hidden]')) focusAfterClose.focus();
    focusAfterClose = null;
  });

  updateCount();
}
